<template>
    <div
        :class="[
            'px-6 pt-4 flex-col flex justify-start items-start bg-white w-full max-w-full flex-wrap mobile-input',
            props.classes,
        ]">
        <div
            class="flex justify-start flex-row flex-wrap w-full flex-start align-start items-center gap-1 mb-2">
            <div
                v-if="label"
                class="flex flex-wrap items-center gap-1 w-full">
                <div
                    class="text-neutral-900 text-base font-bold font-Open w-full">
                    {{ label }}<span v-if="required">*</span>
                    <span
                        v-else
                        class="ml-2 text-sm font-normal"
                        >(optional)</span
                    >
                </div>
            </div>
            <div
                v-if="subtext"
                class="text-neutral-900 text-sm font-normal font-Open leading-tight w-full">
                {{ subtext }}
            </div>
        </div>
        <MazPhoneNumberInput
            class="w-full max-584"
            v-model="phoneNumber"
            show-code-on-list
            color="info"
            :country-code="defaultCountryCode"
            :preferred-countries="['US', 'GB', 'NL', 'SG', 'AE', 'AU', 'HK']"
            @update="handleUpdate"
            @blur="handleBlur"
            :error="showError"
            :error-message="errorMessage"
            :auto-format="false"
             />
        <div
            v-if="showError"
            class="text-red-500 text-sm font-Open w-full">
            Phone number is invalid
        </div>
    </div>
</template>
<script setup lang="ts">
    import MazPhoneNumberInput, {
        CountryCode,
        Results,
    } from 'maz-ui/components/MazPhoneNumberInput';
    import { useBookingStore } from '@/stores/booking';
    import { useValidationStore } from '@/stores/validation';
    import { computed, ref, watch } from 'vue';
    import { MobileInputFieldType } from '../ComponentTypes';

    const bookingStore = useBookingStore();
    const validationStore = useValidationStore();
    const props = defineProps<MobileInputFieldType>();
    const phoneNumber = ref(props.value);
    const hasInteracted = ref(false);
    const isValid = ref(false);

    const errorMessage = computed(() => {
        const storeError = validationStore.getError('phone');
        return storeError || 'Please enter a valid phone number';
    });

    const defaultCountryCode = computed(() => {
        let countryCode =
            bookingStore.state.enquiry_country_code ??
            bookingStore.state.country_code;
        return countryCode as CountryCode;
    });

    const showError = computed(() => {
        // Show error if validation store has an error or
        // if the user has interacted with the field and it's invalid
        const storeHasError = !!validationStore.getError('phone');
        const isInvalid = hasInteracted.value && !isValid.value;

        return storeHasError || isInvalid;
    });

    watch(
        () => props.value,
        (newValue) => {
            if (newValue !== phoneNumber.value) {
                phoneNumber.value = newValue;
            }
        }
    );

    const handleUpdate = (results: Results) => {
        bookingStore.updateBookingState({
            phone: results.formatInternational ?? '',
        });
        isValid.value = results.isValid && !!results.isPossible;
    };

    const handleBlur = () => {
        hasInteracted.value = true;
        validationStore.clearError('phone');
    };
</script>
<style>
    .m-phone-number-input__select {
        @apply w-28 !important;
    }
    .m-phone-number-input__select .m-input-label {
        @apply hidden !important;
    }
    .m-phone-number-input__select .m-select-input .m-input-wrapper {
        @apply justify-end  rounded-b rounded-t !important;
    }
    .m-phone-number-input__input.--border-radius.m-input-wrapper {
        @apply rounded-r !important;
    }
    .m-input.--has-z-2 .m-input-wrapper {
        @apply rounded-r !important;
    }
    .m-input-wrapper-input {
        @apply w-[90%] !important;
    }
    :root
        .m-phone-number-input:not(.--no-flags)
        .m-phone-number-input__select
        .m-select-input
        input {
        @apply pl-[0px] !important;
        @apply pr-[00px] !important;
        @apply ml-[24px] !important;
    }
    :root .m-phone-number-input__country-flag.--should-have-bottom-flag {
        @apply top-2/4 !important;
        @apply left-[15px] !important;
        @apply -translate-y-2/4 !important;
        @apply leading-[0px] !important;
    }
    .m-phone-number-input__select .m-input-wrapper-right {
        @apply p-0 pl-1 !important;
    }
    .m-phone-number-input__select .m-input-wrapper {
        @apply p-3 cursor-pointer !important;
    }
    .m-phone-number-input:not(.--no-flags)
        .m-phone-number-input__select
        .m-input-wrapper
        .m-select-input {
        @apply p-0 pl-9 !important;
    }

    .m-input.--should-up .m-input-label {
        @apply hidden !important;
    }
    .m-input.--has-label .m-input-input {
        @apply pb-0 pt-0 pl-4 pr-4 !important;
    }

    .m-phone-number-input .m-select-input {
        @apply p-0 !important;
    }
    .m-phone-number-input__country-flag {
        @apply bottom-4 !important;
    }
    .maz-border-success {
        @apply border-[#e5e7eb];
    }
</style>
